import type { TextProps } from '@latitude/text';
import { Text } from '@latitude/text';
import { cn } from '@latitude/token-utils';
import type { FC, PropsWithChildren } from 'react';
import type { StatusProps } from 'types';
import { iconVariants, statusVariants } from 'variants';

/**
 * Maps each possible `size` value from `StatusProps` to a corresponding
 * text variant from `TextProps`. This mapping determines which typography
 * style should be used for each status size.
 *
 * - `large` maps to `'headingSmall'`
 * - `medium` maps to `'bodyMedium'`
 * - `small` maps to `'captionMedium'`
 */
const sizeTextVariantMap: Record<
  NonNullable<StatusProps['size']>,
  TextProps['variant']
> = {
  big: 'headingSmall',
  medium: 'bodyMedium',
  tiny: 'captionMedium',
};

/**
 * Maps each possible `status` value from `StatusProps` to a corresponding
 * text color defined in `TextProps['color']`.
 *
 * This mapping is used to determine the appropriate color to display
 * for different status indicators in the UI.
 *
 * - `neutral` maps to `'muted'`
 * - `success` maps to `'success'`
 * - `warning` maps to `'warning'`
 * - `danger` maps to `'danger'`
 */
const statusColorMap: Record<
  NonNullable<StatusProps['status']>,
  TextProps['color']
> = {
  neutral: 'muted',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
  alert: 'alert',
};

/**
 * Renders a status indicator component with optional icon and customizable appearance.
 *
 * @param className - Additional CSS class names to apply to the root element.
 * @param status - The status type to display. Defaults to `'neutral'`.
 * @param size - The size of the status indicator. Defaults to `'medium'`.
 * @param leadingElement - An optional element to display before the status text.
 * @param children - The content to display inside the status component.
 *
 * @remarks
 * The component uses `sizeTextVariantMap` and `statusColorMap` to determine the text style and color
 * based on the provided `size` and `status` props. If `withIcon` is `true`, an icon is rendered
 * using `getStatusIcon`.
 */
const Status: FC<PropsWithChildren<StatusProps>> = ({
  className,
  status = 'neutral',
  size = 'medium',
  leadingElement,
  children,
  ...restProps
}) => {
  const textVariant = sizeTextVariantMap[size];
  const textColor = statusColorMap[status];
  return (
    <div
      className={cn(statusVariants({ size, status }), className)}
      {...restProps}
    >
      {leadingElement && (
        <div className={cn(iconVariants({ size }))}>{leadingElement}</div>
      )}
      <Text variant={textVariant} color={textColor} className={className}>
        {children}
      </Text>
    </div>
  );
};

export { Status, type StatusProps };
